import React from 'react';

import { DateTime } from 'luxon';

import { Nullable } from '@clearverify/types';

import { VerifierType } from 'src/api';
import { DEFAULT_END_DATE, DEFAULT_START_DATE, NOW } from 'src/constants/dashboard';

import { DashboardCVAdminContext, DashboardCVAdminContextValue } from './DashboardCVAdminContext';

type DashboardCVAdminProviderProps = {
  children: React.ReactNode;
};

export const DashboardCVAdminProvider = ({ children }: DashboardCVAdminProviderProps) => {
  const [date, setDate] = React.useState<DashboardCVAdminContextValue['date']>(() => NOW);

  const [employers, setEmployers] = React.useState<DashboardCVAdminContextValue['employers']>([]);

  const [peos, setPeos] = React.useState<DashboardCVAdminContextValue['peos']>([]);

  const [usageReportStartDate, setUsageReportStartDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_START_DATE);

  const [usageReportEndDate, setUsageReportEndDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_END_DATE);

  const [workLetterUsageStartDate, setWorkLetterUsageStartDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_START_DATE);

  const [workLetterUsageEndDate, setWorkLetterUsageEndDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_END_DATE);

  const [revenueByVerificationTypeStartDate, setRevenueByVerificationTypeStartDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_START_DATE);

  const [revenueByVerificationTypeEndDate, setRevenueByVerificationTypeEndDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_END_DATE);

  const [revenueDistributionsStartDate, setRevenueDistributionsStartDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_START_DATE);

  const [revenueDistributionsEndDate, setRevenueDistributionsEndDate] =
    React.useState<Nullable<DateTime>>(DEFAULT_END_DATE);

  const [verifierType, setVerifierType] = React.useState<VerifierType>(VerifierType.Commercial);

  const value = React.useMemo(
    () => ({
      date,
      setDate,

      employers: employers?.[0] ? employers : undefined,
      setEmployers,

      peos: peos?.[0] ? peos : undefined,
      setPeos,

      usageReportStartDate,
      setUsageReportStartDate,

      usageReportEndDate,
      setUsageReportEndDate,

      workLetterUsageStartDate,
      setWorkLetterUsageStartDate,

      workLetterUsageEndDate,
      setWorkLetterUsageEndDate,

      revenueByVerificationTypeStartDate,
      setRevenueByVerificationTypeStartDate,

      revenueByVerificationTypeEndDate,
      setRevenueByVerificationTypeEndDate,

      revenueDistributionsStartDate,
      setRevenueDistributionsStartDate,

      revenueDistributionsEndDate,
      setRevenueDistributionsEndDate,

      verifierType,
      setVerifierType,
    }),
    [
      date,
      peos,
      employers,
      revenueByVerificationTypeEndDate,
      revenueByVerificationTypeStartDate,
      revenueDistributionsEndDate,
      revenueDistributionsStartDate,
      usageReportEndDate,
      usageReportStartDate,
      verifierType,
      workLetterUsageEndDate,
      workLetterUsageStartDate,
    ],
  );

  return (
    <DashboardCVAdminContext.Provider value={value}>{children}</DashboardCVAdminContext.Provider>
  );
};
